import React, { useEffect, useMemo, useState } from 'react'
import {
    loadClassDetailedEmotionsAsync,
    loadCounsellorDetailedEmotionsAsync,
    loadOrganizationDetailedEmotionsAsync,
    loadStudentDetailedEmotionsAsync,
} from '../../../server/server'
import { useGetAnalyticTypeFromPath, useHasStudentInPath, useQuery } from '../../../utils/util'
import BarChartDetailsOverall from './BarChartDetailsOverall'
import { useTranslation } from 'react-i18next'
import { calculateDateRange } from '../../../utils/dateUtils'
import InfoButton from '../../common/InfoButton'

function DetailsOnEmotions({ daysFilter, student, isCounsellor, selectedUsersScope, analyticsType }) {
    const { t } = useTranslation()
    const query = useQuery()
    const currentId = query.get('id')
    const [loading, setLoading] = useState(true)
    const [data, setData] = useState(null)
    const isStudentAnalytics = useHasStudentInPath()
    const analyticType = useGetAnalyticTypeFromPath()

    useEffect(() => {
        setLoading(true)
        loadData()
    }, [currentId, daysFilter, selectedUsersScope])

    const loadData = async () => {
        let callFunction
        switch (analyticsType) {
            case 'student':
                callFunction = loadStudentDetailedEmotionsAsync
                break
            case 'counsellor':
                callFunction = loadCounsellorDetailedEmotionsAsync
                break
            case 'organization':
                callFunction = loadOrganizationDetailedEmotionsAsync
                break
            default:
                callFunction = loadClassDetailedEmotionsAsync
        }
        const { startDate, endDate } = calculateDateRange(daysFilter)
        const resp = await callFunction(isCounsellor ? selectedUsersScope : currentId, startDate, endDate)
        if (resp.status === 200) {
            const transformedList = transformedData(resp.data ?? [])
            setData(transformedList)
        } else {
            setData(null)
        }
        setLoading(false)
    }

    const transformedData = (list) => {
        const newList = list.map((item) => {
            const total = item.comfortable + item.uncomfortable
            const percentage_comfortable = total > 0 ? (item.comfortable / total) * 100 : 0
            const percentage_uncomfortable = total > 0 ? (item.uncomfortable / total) * 100 : 0
            const comfortableEmotions = item?.feedback?.filter((emotion) => emotion.comfortable)
            const uncomfortableEmotions = item?.feedback?.filter((emotion) => !emotion.comfortable)

            return {
                id: item._id,
                label_name: item.label_name,
                comfortable: item.comfortable,
                uncomfortable: item.uncomfortable,
                total_emotions: item.total_emotions,
                percentage_comfortable: parseFloat(percentage_comfortable.toFixed(2)),
                percentage_uncomfortable: parseFloat(percentage_uncomfortable.toFixed(2)),
                comfortableEmotions,
                uncomfortableEmotions,
            }
        })
        return newList
    }

    const getTitleByAnalyticType = (analyticType) => {
        const titles = {
            student: t('Emotional Trends'),
            class: t('Class Emotional Trends'),
            counsellor: t('Emotional trends for selected student groups'),
            organization: t('Emotional trends for selected student groups'),
        }
        return titles[analyticType] || t('Emotional Trends')
    }

    const getInfoByAnalyticType = (analyticType) => {
        const titles = {
            student: t(
                'This section shows trends in the student’s emotional check-ins over time. It highlights the percentage of comfortable and uncomfortable emotions, along with the time frame and number of check-ins. Key insights, such as the highest levels of comfortable and uncomfortable emotions, are displayed for better understanding.'
            ),
            class: t(`Here you'll see the class's emotional wellbeing over the selected period, showing the breakdown of comfortable and uncomfortable emotions`),
            counsellor: t('View trends in comfortable and uncomfortable emotions to spot key patterns and peaks.'),
            organization: t('View trends in comfortable and uncomfortable emotions to spot key patterns and peaks.'),
        }
        return titles[analyticType] || t('View trends in comfortable and uncomfortable emotions to spot key patterns and peaks.')
    }

    return (
        <div className="bg-white rounded-[24px] py-5 px-[18px] mt-4" data-tour="emotional_trends">
            <div className=" flex flex-row items-center justify-between">
                <div className="flex flex-row items-center gap-2">
                    <p className="text-[18px] leading-[25px] font-medium text-darkenPurple ">{getTitleByAnalyticType(analyticsType)}</p>
                    <div className="flex items-center justify-center rounded-full bg-[#413F5E14] h-6 px-[10px]">
                        <p className="text-[13px] leading-[18px] font-normal text-strongGray ">{t(daysFilter.label)}</p>
                    </div>
                </div>
                <button>
                    <InfoButton color="#5E639B" text={getInfoByAnalyticType(analyticsType)} />
                </button>
            </div>
            <BarChartDetailsOverall loading={loading} data={data} student={student} daysFilter={daysFilter} />
        </div>
    )
}

export default DetailsOnEmotions
